// Súhrn merateľných zápisov {ISO deň: hodnota} do tvaru, ktorý kreslí MetricStatsView
// (graf posledných dní + priemer / rekord / spolu / tento týždeň).
//
// Pre NÁVYK to isté počíta backend (`GET /api/habit-metric-stats`, viď app.py) — má k tomu
// completions (splnený denný cieľ). SÚŤAŽ má moje denné zápisy už v detaile (`my_days`),
// tak sa počítajú tu, bez ďalšieho requestu. Pravidlá sú rovnaké ako na backende:
// priemer a rekord počítajú len DNI SO ZÁPISOM; týždne sú kalendárne (Po–Ne).
import { shiftDay } from "./format";

export type MetricDay = { day: string; amount: number; done: boolean };
export type MetricSummary = {
  days: MetricDay[];
  window_days: number;
  first_window_day: string;
  last_window_day: string;
  total: number;
  logged: number;
  logged_window: number;
  avg: number;
  best: { day: string; amount: number } | null;
  first_day: string | null;
  week: number;
  week_prev: number;
};

const mondayOf = (key: string): string => {
  const d = new Date(key + "T00:00:00");
  return shiftDay(key, -((d.getDay() + 6) % 7));   // getDay: Ne=0 → pondelok je o (day+6)%7 späť
};

// amounts: {deň: hodnota} · lastDay: posledný deň okna (dnes, alebo koniec súťaže, keď už
// skončila) · firstDay: nepovinná spodná hranica (štart súťaže) — okno sa nikdy nekreslí pred ňu,
// ale ostáva aspoň `minDays` široké, nech jednodňová súťaž nemá graf z jedného stĺpca.
export function summarizeMetric(
  amounts: Record<string, number | null | undefined>,
  lastDay: string,
  opts: { firstDay?: string | null; windowDays?: number; minDays?: number; doneDays?: Set<string> } = {},
): MetricSummary {
  const windowDays = opts.windowDays ?? 30;
  const minDays = opts.minDays ?? 7;
  const logged: Record<string, number> = {};
  for (const [k, v] of Object.entries(amounts || {})) {
    const n = Number(v || 0);
    if (n > 0) logged[k] = n;
  }
  let first = shiftDay(lastDay, -(windowDays - 1));
  if (opts.firstDay && opts.firstDay > first) {
    const capped = shiftDay(lastDay, -(minDays - 1));
    first = opts.firstDay < capped ? opts.firstDay : capped;
  }
  const days: MetricDay[] = [];
  for (let k = first; k <= lastDay; k = shiftDay(k, 1)) {
    const amount = logged[k] || 0;
    days.push({ day: k, amount, done: opts.doneDays ? opts.doneDays.has(k) : amount > 0 });
  }
  const keys = Object.keys(logged).sort();
  const total = keys.reduce((s, k) => s + logged[k], 0);
  let best: MetricSummary["best"] = null;
  for (const k of keys) if (!best || logged[k] >= best.amount) best = { day: k, amount: logged[k] };
  const wkStart = mondayOf(lastDay);
  const prevStart = shiftDay(wkStart, -7);
  const prevEnd = shiftDay(wkStart, -1);
  const sum = (lo: string, hi: string) => keys.filter((k) => k >= lo && k <= hi).reduce((s, k) => s + logged[k], 0);
  return {
    days,
    window_days: days.length,
    first_window_day: first,
    last_window_day: lastDay,
    total,
    logged: keys.length,
    logged_window: days.filter((x) => x.amount > 0).length,
    avg: keys.length ? Math.round((total / keys.length) * 10) / 10 : 0,
    best,
    first_day: keys[0] || null,
    week: sum(wkStart, lastDay),
    week_prev: sum(prevStart, prevEnd),
  };
}
